import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FiSearch, FiLayers, FiBook, FiList, FiAlertCircle } from 'react-icons/fi';
import PublicationItem from '../components/PublicationItem';
import SEO from '../components/SEO';
import '../styles/publications.css';

const PublicationsPage = () => {
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortBy, setSortBy] = useState('newest');

    useEffect(() => {
        fetch('/publications.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Failed to fetch publications data');
                }
                return response.json();
            })
            .then(data => {
                setPublications(data);
                setLoading(false);
            })
            .catch(err => {
                console.error("Error fetching publications:", err);
                setError(err.message);
                setLoading(false);
            });
    }, []);

    const filteredAndSortedPublications = useMemo(() => {
        let result = [...publications];

        // Filter
        if (searchQuery) {
            const lowerQuery = searchQuery.toLowerCase();
            result = result.filter(pub =>
                pub.title.toLowerCase().includes(lowerQuery) ||
                (pub.authors && pub.authors.some(author => author.toLowerCase().includes(lowerQuery))) ||
                pub.journal.toLowerCase().includes(lowerQuery)
            );
        }

        // Sort
        result.sort((a, b) => {
            if (sortBy === 'newest') {
                if (b.year !== a.year) return b.year - a.year;
                return (b.month || 0) - (a.month || 0);
            }
            if (sortBy === 'oldest') {
                if (a.year !== b.year) return a.year - b.year;
                return (a.month || 0) - (b.month || 0);
            }
            if (sortBy === 'title') return a.title.localeCompare(b.title);
            return 0;
        });

        return result;
    }, [publications, searchQuery, sortBy]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <Container className="py-5 text-center">
                <div className="alert alert-danger d-inline-block">
                    <FiAlertCircle className="me-2" />
                    Error loading publications: {error}
                </div>
            </Container>
        );
    }

    return (
        <div className="publications-bento-container py-5">
            <SEO
                title="Publications"
                description="Explore our research, datasets, and technical reports on electrospinning."
            />

            {/* Header Section */}
            <header className="text-center mb-5 bento-header">
                <h1 className="display-4 font-weight-bold">Publications</h1>
                <p className="lead text-muted pub-intro-bento">
                    Explore our research, datasets, and technical reports promoting transparency in materials science.
                </p>
            </header>

            <Container style={{ maxWidth: '1000px' }}>
                {/* Search & Filter Card */}
                <div className="pub-search-card mb-5">
                    <Row className="g-3 align-items-center">
                        <Col md={8}>
                            <div className="search-input-group">
                                <FiSearch className="search-icon" />
                                <Form.Control
                                    type="text"
                                    placeholder="Search by title, author, or journal..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="pub-search-field"
                                />
                            </div>
                        </Col>
                        <Col md={4}>
                            <div className="d-flex align-items-center gap-2">
                                <FiList className="text-muted" />
                                <Form.Select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="pub-sort-select"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                    <option value="title">Title (A-Z)</option>
                                </Form.Select>
                            </div>
                        </Col>
                    </Row>
                </div>

                {/* Publications List */}
                <div className="publications-list">
                    {filteredAndSortedPublications.length === 0 ? (
                        <div className="empty-publications">
                            <FiBook className="empty-icon" />
                            <h3 className="fw-bold text-dark mb-2">No publications found</h3>
                            <p className="text-muted">We couldn't find any results matching "{searchQuery}". Try a different search term.</p>
                        </div>
                    ) : (
                        filteredAndSortedPublications.map(pub => (
                            <PublicationItem key={pub.id} publication={pub} />
                        ))
                    )}
                </div>
            </Container>
        </div>
    );
};

export default PublicationsPage;
